import { DistributeSystemDashboardCommandResult } from "@ternary/api-lib/core/commands/DistributeSystemDashboardCommand";
import { DistributeSystemReportCommandResult } from "@ternary/api-lib/core/commands/DistributeSystemReportCommand";
import { GrantUsersGlobalAccessCommandResult } from "@ternary/api-lib/core/commands/GrantUsersGlobalAccessCommand";
import { RemoveSystemDashboardCommandResult } from "@ternary/api-lib/core/commands/RemoveSystemDashboardCommand";
import { RemoveSystemReportCommandResult } from "@ternary/api-lib/core/commands/RemoveSystemReportCommand";
import { RevokeUserGlobalAccessCommandResult } from "@ternary/api-lib/core/commands/RevokeUserGlobalAccessCommand";
import { FindRolesQueryResult } from "@ternary/api-lib/core/queries/FindRolesQuery";
import { FindUsersQueryResult } from "@ternary/api-lib/core/queries/FindUsersQuery";
import { EventReporter } from "@ternary/api-lib/telemetry";
import HTTPRequestSender from "../HTTPRequestSender";

export type GrantUsersGlobalAccessParams = {
  users: {
    userID?: string;
    email: string;
    roleIDs: string[];
  }[];
};

interface Config {
  eventReporter: EventReporter;
  sender: HTTPRequestSender;
}

export class InternalApiClient {
  private readonly _eventReporter: EventReporter;
  private readonly _sender: HTTPRequestSender;

  constructor({ sender, eventReporter }: Config) {
    this._sender = sender;
    this._eventReporter = eventReporter;
  }

  public async distributeSystemDashboard(dashboardID: string): Promise<string> {
    try {
      const result = (await this._sender.send(
        `/dashboards/distribute/${dashboardID}`,
        { method: "POST" }
      )) as DistributeSystemDashboardCommandResult;

      return result.dashboardID;
    } catch (error) {
      this._eventReporter.reportError(error);

      throw new Error("ERROR_DISTRIBUTING_SYSTEM_DASHBOARD");
    }
  }

  public async distributeSystemReport(reportID: string): Promise<string> {
    try {
      const result = (await this._sender.send(
        `/reports/distribute/${reportID}`,
        { method: "POST" }
      )) as DistributeSystemReportCommandResult;

      return result.reportID;
    } catch (error) {
      this._eventReporter.reportError(error);

      throw new Error("ERROR_DISTRIBUTING_SYSTEM_REPORT");
    }
  }

  public async getRoles(): Promise<FindRolesQueryResult["roles"]> {
    try {
      const result = (await this._sender.send(
        `/roles`
      )) as FindRolesQueryResult;

      return result.roles;
    } catch (error) {
      this._eventReporter.reportError(error);

      throw new Error("ERROR_LOADING_ROLES");
    }
  }

  public async getUsers(): Promise<FindUsersQueryResult["users"]> {
    try {
      const result = (await this._sender.send(
        `/users`
      )) as FindUsersQueryResult;

      return result.users;
    } catch (error) {
      this._eventReporter.reportError(error);

      throw new Error("ERROR_LOADING_USERS");
    }
  }

  public async grantUsersGlobalAccess(
    params: GrantUsersGlobalAccessParams
  ): Promise<string[]> {
    try {
      const result = (await this._sender.send(`/users/grant-global-access`, {
        method: "POST",
        body: params,
      })) as GrantUsersGlobalAccessCommandResult;

      return result.userIDs;
    } catch (error) {
      this._eventReporter.reportError(error);

      throw new Error("ERROR_GRANTING_USERS_GLOBAL_ACCESS");
    }
  }

  public async removeSystemDashboard(dashboardID: string): Promise<string> {
    try {
      const result = (await this._sender.send(
        `/dashboards/remove/${dashboardID}`,
        { method: "POST" }
      )) as RemoveSystemDashboardCommandResult;

      return result.dashboardID;
    } catch (error) {
      this._eventReporter.reportError(error);

      throw new Error("ERROR_REMOVING_SYSTEM_DASHBOARD");
    }
  }

  public async removeSystemReport(reportID: string): Promise<string> {
    try {
      const result = (await this._sender.send(`/reports/remove/${reportID}`, {
        method: "POST",
      })) as RemoveSystemReportCommandResult;

      return result.reportID;
    } catch (error) {
      this._eventReporter.reportError(error);

      throw new Error("ERROR_REMOVING_SYSTEM_REPORT");
    }
  }

  public async revokeUserGlobalAccess(params: {
    userID: string;
  }): Promise<string> {
    try {
      const result = (await this._sender.send(`/users/revoke-global-access`, {
        method: "POST",
        body: params,
      })) as RevokeUserGlobalAccessCommandResult;

      return result.userID;
    } catch (error) {
      this._eventReporter.reportError(error);

      throw new Error("ERROR_REVOKING_USER_GLOBAL_ACCESS");
    }
  }
}
